import moment from 'moment-timezone';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Confirm,
  Dropdown,
  Grid,
  Icon,
  Menu,
  Loader,
  Popup,
  Table,
  GridRow,
  GridColumn,
  Item,
  Label
} from 'semantic-ui-react';
import styled from 'styled-components';
import ModalAttachFiles from '../../../components/ModalAttachFiles';
import {
  getFireInsuranceEstatesReport,
  getFireInsuranceModelAlfaReport,
  getFireInsuranceReport,
  getPolicies,
  getPolicyInfo,
  getPolicyInfoAwaitingBilling,
  retrySegimobHire,
  retryYelumHire
} from '../../../services/fire-insurance-policy';
import { maskCpfOrCnpj, maskMoney, numberToMoneyString } from '../../../services/masks';
import { FieldLabel } from '../../../styles';
import {
  ExpireInList,
  fileIdentifiers,
  proposalStatusList,
  translateProposalStatus
} from '../utils/Pottencial';
import DetailModal from './Details';
import { connect, useSelector } from 'react-redux';
import { iRootDispatch, iRootState } from '../../../store';
import {
  AvailableBrokersAndEstatesState,
  CurrentPageState
} from '../../../store/types/temp-types';
import { CustoDropDownItemProps } from '../../../components/types';
import AuthorizationContainer from '../../../components/AuthorizationContainer';
import { ActionEnum } from '../../../enums/authz-action.enum';
import { FeatureEnum } from '../../../enums/authz-feature.enum';
import CancelPolicyModal from '../budget-list/Cancel/CancelPolicyModal';
import IconButton from '../../../components/IconButton';
import { useHistory, useLocation } from 'react-router-dom';
import EditCancelPolicyModal from '../budget-list/Cancel/EditCancelPolicyModal';
import EditPolicyModal, { EditPolicyModalData } from './EditPolicyModal';
import ConsultativeIssueModal from './ConsultativeIssueModal';
import AdminEditPolicyModal, {
  AdminEditPolicyModalData
} from './AdminEditPolicyModal';
import { InsurersCodeEnum } from '../../../enums/insurers-code.enum';
import { getCanceled } from '../../../services/fire-insurance-quotation';
import { dangerNotification, successNotification, warningNotification } from '../../../services/notification';
import { ReportModal } from '../../../components/ReportModal';
import { isUuid } from 'uuidv4';
import FireGeneralCondition from '../budget/GeneralConditionModal';
import { adjustDateForTimezone } from '../../../utils/AjustDates';
import { get } from '../../../services/storage';
import { typeOptions } from './types';
import { UserState } from '../../../types/UserState';
import { useDropDownUsers } from '../../../services/broker';
import { PushRoute, UsersToCustomDropDownOptions } from '../../../util';
import { BudgetContext } from '../budget/forms-based-in-insurer/Alfa';
import { isBackOfficeUser } from '../../../utils/isBackOfficeUser';
import TablePaginationMenu from '../../../components/TablePaginationMenu';
import Button from '../../../components/Button';
import PopupModal from '../../../components/PopupModal';
import { BannerLocationActivationEnum } from '../../../types/Banner';
import LenderInsuranceUpdateModal from '../components/LenderInsuranceUpdateModal';
import { calculateAge } from '../../../utils/calculateAge';
import EstateUnitFilter from '../../../components/EstateUnitFilter';
import { appendEstateUnitIdToFilter } from '../../../utils/estateUnitFilter.util';
import { getListEmptyMessage } from '../../../utils/estateUnitEmptyState.util';
import { UserRolesEnum } from '../../../enums/user-roles.enum';
import FilterBar, {
  FilterField,
  FilterValues,
  toDateOnlyString,
  toFilterIdList
} from '../../../components/Filter';

const ClickableTableCell = styled(Table.Cell)`
  cursor: pointer;
  position: relative;
`;

const ClickableTableRow = styled(Table.Row)`
  :hover {
    background-color: #f0f0f0;
  }
`;

type SearchField = 'document' | 'name' | 'address';

type ApiFilters = {
  id?: string;
  fireInsuranceId?: string;
  insurerId?: string | string[];
  estateId?: string;
  status?: string | string[];
  vigency?: string;
  accountManager?: string;
  start?: string;
  end?: string;
  estateUnitId?: string;
  document?: string;
  name?: string;
  address?: string;
  isRv?: boolean;
  page?: number;
};

const SEARCH_FIELDS: SearchField[] = ['document', 'name', 'address'];

type Props = {
  user: UserState;
  availableBrokersAndEstates: AvailableBrokersAndEstatesState;
  updateCurrentPage: Function;
};

const toSelectOptions = (
  items: Array<{ value: any; text: string }>
): Array<{ value: string; label: string }> =>
  items.map(item => ({
    value: String(item.value),
    label: String(item.text)
  }));

const formatDateValue = (value: any): string | undefined => {
  const dateOnly = toDateOnlyString(value);
  return dateOnly || undefined;
};

const normalizeToApiFilter = (values: FilterValues): ApiFilters => {
  const searchType = (values.type as SearchField) || 'document';
  let argument = String(values.argument || '').trim();

  if (searchType === 'document' && argument) {
    argument = argument.replace(/\D/g, '');
  }

  const filter: ApiFilters = {
    isRv: true
  };

  SEARCH_FIELDS.forEach(field => {
    filter[field] = undefined;
  });

  if (searchType && argument && SEARCH_FIELDS.includes(searchType)) {
    filter[searchType] = argument;
  }

  const insurerIds = toFilterIdList(values.insurerId);
  if (insurerIds) filter.insurerId = insurerIds;
  if (values.estateId) filter.estateId = String(values.estateId);
  const statuses = toFilterIdList(values.status);
  if (statuses) filter.status = statuses;
  if (values.vigency) filter.vigency = String(values.vigency);

  const start = formatDateValue(values.start);
  const end = formatDateValue(values.end);
  if (start) filter.start = start;
  if (end) filter.end = end;

  if (values.accountManager) {
    filter.accountManager = String(values.accountManager);
  }

  return appendEstateUnitIdToFilter(
    filter as Record<string, unknown>,
    String(values.estateUnitId || '')
  ) as ApiFilters;
};

const FireInsurancePolicy = (props: Props) => {
  const { availableBrokersAndEstates, user, updateCurrentPage } = props;
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [policyCancelation, setPolicyCancelation] = useState<any>(null);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openCancelModal, setOpenCancelModal] = useState<boolean>(false);
  const [openEditCancelModal, setOpenEditCancelModal] = useState<boolean>(false);
  const [editCancelData, setEditCancelData] = useState<any>(undefined);
  const [openEditPolicyModal, setOpenEditPolicyModal] = useState<boolean>(false);
  const [editPolicyData, setEditPolicyData] = useState<EditPolicyModalData | undefined>(
    undefined
  );
  const [openAdminEditPolicyModal, setOpenAdminEditPolicyModal] = useState<boolean>(false);
  const [adminEditPolicyData, setAdminEditPolicyData] = useState<
    AdminEditPolicyModalData | undefined
  >(undefined);

  const [consultativeIssueItem, setConsultativeIssueItem] = useState<any>(null);
  const [openLenderInsuranceModal, setOpenLenderInsuranceModal] = useState<boolean>(false);
  const [selectedAnalysis, setSelectedAnalysis] = useState<any>(null);
  const [lenderInsuranceRate, setLenderInsuranceRate] = useState<number>(0);
  const [retryHireConfirmOpen, setRetryHireConfirmOpen] = useState<boolean>(false);
  const [policyToRetryHire, setPolicyToRetryHire] = useState<any>(null);
  const [isRetryingHire, setIsRetryingHire] = useState<boolean>(false);

  const handleOpenLenderInsuranceModal = (analysis: any) => {
    setSelectedAnalysis(analysis);
    if (analysis?.fireInsurance?.estate?.lenderInsuranceRate) {
      setLenderInsuranceRate(Number(analysis.fireInsurance.estate.lenderInsuranceRate));
    }
    setOpenLenderInsuranceModal(true);
  };

  const handleCloseLenderInsuranceModal = () => {
    setOpenLenderInsuranceModal(false);
    setSelectedAnalysis(null);
  };

  const onSuccessLenderInsurance = () => {
    // Refresh list
    getData();
  };

  // Check if lender insurance is enabled for the estate
  const lenderInsuranceEnabled = useSelector(
    (state: iRootState) => state.availableBrokersAndEstates.lenderInsuranceEnabled ?? false
  );
  const [currentPolicyId, setCurrentPolicyId] = useState<string>('');
  const [currentInsurerCode, setCurrentInsurerCode] = useState<string>('');
  const [currentPolicyData, setCurrentPolicyData] = useState<any>(null);
  const [currentDetailPolicy, setCurrentDetailPolicy] = useState<any>(null);
  const [infoAwaitingBilling, setInfoAwaitingBilling] = useState<any>({
    qty: '-',
    amount: '-',
    batchClosingDay: []
  });
  const [popupString, setPopupString] = useState<string>('');
  const [data, setData] = useState<any[]>([]);
  const [count, setCount] = useState<number>(0);
  const [reportModalIsOpen, setReportModalIsOpen] = useState<boolean>(false);
  const [reportState, setReportState] = useState<number | undefined>(undefined);
  const [openConditions, setOpenConditions] = useState<boolean>(false);

  const location = useLocation();
  const history = useHistory();

  const userRole = get('role');
  const isBackOffice = isBackOfficeUser();

  const estates: CustoDropDownItemProps[] = availableBrokersAndEstates.estates;
  const insurerRecords: CustoDropDownItemProps[] =
    availableBrokersAndEstates.insurers.filter(insurer =>
      insurer.value?.availability?.includes('FIRE_INSURANCE')
    );

  const singleEstateId = useMemo(() => {
    if (estates.length !== 1) return '';
    const value = estates[0]?.value;
    return String(typeof value === 'object' && value?.id ? value.id : value || '');
  }, [estates]);

  const estateOptions = useMemo(
    () =>
      estates.map(item => {
        const valueId =
          typeof item.value === 'object' && item.value?.id
            ? item.value.id
            : item.value;
        return {
          value: String(valueId || ''),
          text: String(item.text || '')
        };
      }),
    [estates]
  );

  const estateRecordsById = useMemo(() => {
    const map: Record<string, any> = {};
    estates.forEach(item => {
      const record = typeof item.value === 'object' ? item.value : { id: item.value };
      if (record?.id) map[String(record.id)] = record;
    });
    return map;
  }, [estates]);

  const insurerOptions = useMemo(
    () =>
      insurerRecords.map(item => ({
        value: String(item.value?.id || ''),
        text: String(item.text || '')
      })),
    [insurerRecords]
  );

  // Deep-links: path UUID (analysis id) and ?fireInsuranceId= query param are kept as external state
  const initialSearchParams = new URLSearchParams(location.search);
  const initialFireInsuranceIdFromQuery = initialSearchParams.get('fireInsuranceId');

  const initialPathParts = history.location.pathname.split('/');
  const initialLastPathSegment = initialPathParts[initialPathParts.length - 1];
  const initialFireInsuranceAnalysisId = isUuid(initialLastPathSegment)
    ? initialLastPathSegment
    : null;

  const hasDeepLink = Boolean(
    initialFireInsuranceAnalysisId || initialFireInsuranceIdFromQuery
  );

  const [fireInsuranceAnalysisId, setFireInsuranceAnalysisId] = useState<string | null>(
    initialFireInsuranceAnalysisId
  );
  const [fireInsuranceId, setFireInsuranceId] = useState<string | null>(
    initialFireInsuranceIdFromQuery
  );
  const [isEnabledToFetch, setIsEnabledToFetch] = useState(hasDeepLink || !isBackOffice);

  const initialFilterValues = useMemo(
    (): FilterValues => ({
      type: 'document',
      argument: '',
      insurerId: [],
      estateId: singleEstateId,
      status: [],
      start: '',
      end: '',
      vigency: '',
      estateUnitId: '',
      accountManager: ''
    }),
    [singleEstateId]
  );

  const [appliedFilters, setAppliedFilters] = useState<ApiFilters>(() => {
    const initial = normalizeToApiFilter(initialFilterValues);
    if (!initial.estateId && singleEstateId) {
      initial.estateId = singleEstateId;
    }
    return initial;
  });

  const appliedFiltersRef = useRef(appliedFilters);
  const fireInsuranceAnalysisIdRef = useRef(fireInsuranceAnalysisId);
  const fireInsuranceIdRef = useRef(fireInsuranceId);

  useEffect(() => {
    appliedFiltersRef.current = appliedFilters;
  }, [appliedFilters]);

  useEffect(() => {
    fireInsuranceAnalysisIdRef.current = fireInsuranceAnalysisId;
  }, [fireInsuranceAnalysisId]);

  useEffect(() => {
    fireInsuranceIdRef.current = fireInsuranceId;
  }, [fireInsuranceId]);

  const toggleOpenConditions = () => {
    setOpenConditions(true);
  };

  const toggleCloseConditions = () => {
    setOpenConditions(false);
  };

  const getData = useCallback(async () => {
    setIsLoadingData(true);

    const filter: ApiFilters = {
      ...appliedFiltersRef.current,
      id: fireInsuranceAnalysisIdRef.current || undefined,
      fireInsuranceId: fireInsuranceIdRef.current || undefined,
      page: currentPage,
      isRv: true
    };

    if (!filter.estateId && singleEstateId) {
      filter.estateId = singleEstateId;
    }

    const data = await getPolicies(filter);

    setData(data?.data);
    setCount(data?.quantity);
    setIsLoadingData(false);
  }, [currentPage, singleEstateId]);

  const getDataPolicyInfoAwaitingBilling = async (estateId: string) => {
    const response = await getPolicyInfoAwaitingBilling(estateId);

    if (response) {
      setInfoAwaitingBilling({
        qty: response.qty,
        amount: numberToMoneyString(response.amount),
        batchClosingDay: response.batchClosingDay
      });
    } else {
      setInfoAwaitingBilling({
        qty: '-',
        amount: '-',
        batchClosingDay: []
      });
    }
  };

  const handleCloseModal = () => {
    setCurrentDetailPolicy(null);
    setPolicyCancelation(null);
    setOpenModal(false);
  };

  const handleCloseEditCancelModal = () => {
    setOpenEditCancelModal(false);
  };

  const getCancelation = async (id: string) => {
    let response: any;

    try {
      response = await getCanceled(id);
    } catch (err) {
      console.log({ err });
    } finally {
      setPolicyCancelation(response);
    }
  };

  const getDetails = async (id: string) => {
    let response: any;

    try {
      response = await getPolicyInfo(id);
    } catch (err) {
      console.log({ err });
    } finally {
      return response;
    }
  };

  const renderDetailModal = async (item: any) => {
    if (['CANCELED', 'CANCELATION_REQUEST'].includes(item.status)) {
      await getCancelation(item.fireInsuranceAnalysis.id);
    }

    const policy = await getDetails(item.id);
    setCurrentDetailPolicy(policy);
    setOpenModal(true);
  };

  const handleOpenCancelModal = (item: any) => {
    setCurrentPolicyId(item.id);
    setCurrentInsurerCode(item.fireInsuranceAnalysis.insurer.code);
    setCurrentPolicyData(item);
    setOpenCancelModal(true);
  };

  const handleOpenEditCancelModal = (item: any) => {
    const data = {
      status: item.status,
      policyId: item.id,
      analysisId: item.fireInsuranceAnalysis.id,
      insurerCode: item.fireInsuranceAnalysis.insurer.code
    };

    setEditCancelData(data);
    setOpenEditCancelModal(true);
  };

  const canEditPolicy = (item: any) =>
    item.status === 'PROPOSAL_WAITING' &&
    item.fireInsuranceAnalysis?.insurer?.code === InsurersCodeEnum.TokioMarineSegimob &&
    ['warning', 'error'].includes(item.fireInsuranceAnalysis?.validationStatus);

  const isSegimobFireInsurerCode = (code?: string) =>
    !!code &&
    [
      InsurersCodeEnum.TokioMarineSegimob,
      InsurersCodeEnum.Zurich,
      InsurersCodeEnum.Liberty
    ].includes(code as InsurersCodeEnum);

  const canRetrySegimobHire = (item: any) =>
    userRole === UserRolesEnum.brokerAdmin &&
    item.status === 'PROPOSAL_WAITING' &&
    isSegimobFireInsurerCode(item.fireInsuranceAnalysis?.insurer?.code);

  const canRetryYelumHire = (item: any) =>
    userRole === UserRolesEnum.brokerAdmin &&
    item.status === 'ERROR' &&
    item.fireInsuranceAnalysis?.insurer?.code === InsurersCodeEnum.Yelum;

  const canRetryHire = (item: any) =>
    canRetrySegimobHire(item) || canRetryYelumHire(item);

  const handleOpenRetryHireConfirm = (item: any) => {
    setPolicyToRetryHire(item);
    setRetryHireConfirmOpen(true);
  };

  const handleCloseRetryHireConfirm = () => {
    if (isRetryingHire) {
      return;
    }

    setRetryHireConfirmOpen(false);
    setPolicyToRetryHire(null);
  };

  const handleConfirmRetryHire = async () => {
    if (!policyToRetryHire?.id || isRetryingHire) {
      return;
    }

    setIsRetryingHire(true);

    try {
      if (canRetryYelumHire(policyToRetryHire)) {
        await retryYelumHire(policyToRetryHire.id);
      } else {
        await retrySegimobHire(policyToRetryHire.id);
      }
      successNotification('Sucesso', 'Nova tentativa de contratação iniciada');
      setRetryHireConfirmOpen(false);
      setPolicyToRetryHire(null);
      await getData();
    } catch (error) {
      dangerNotification(
        'Ops...',
        (error as any)?.message || 'Não foi possível iniciar a nova tentativa de contratação'
      );
    } finally {
      setIsRetryingHire(false);
    }
  };

  const handleOpenEditPolicyModal = (item: any) => {
    const fireInsurance = item.fireInsuranceAnalysis?.fireInsurance;

    setEditPolicyData({
      policyId: item.id,
      tenantName: fireInsurance?.tenantName ?? '',
      tenantDocument: fireInsurance?.tenantDocument ?? '',
      tenantEmail: fireInsurance?.tenantEmail ?? '',
      tenantCellphone: fireInsurance?.tenantCellphone ?? ''
    });
    setOpenEditPolicyModal(true);
  };

  const handleCloseEditPolicyModal = () => {
    setOpenEditPolicyModal(false);
    setEditPolicyData(undefined);
  };

  const handleOpenAdminEditPolicyModal = async (item: any) => {
    let policy = item;
    try {
      const details = await getPolicyInfo(item.id);
      if (details) {
        policy = details;
      }
    } catch (error) {
      console.log(error);
    }

    const analysis = policy.fireInsuranceAnalysis || item.fireInsuranceAnalysis || {};

    setAdminEditPolicyData({
      policyId: policy.id || item.id,
      analysisId: analysis.id,
      status: policy.status || item.status,
      policyNumber: policy.policyNumber,
      externalProposalCode: policy.externalProposalCode,
      externalPolicyCode: policy.externalPolicyCode,
      referenceCode: analysis.referenceCode,
      observations: policy.observations,
      paymentType: policy.paymentType,
      installments: policy.installments,
      policyPeriodStart: policy.policyPeriodStart || analysis.policyPeriodStart,
      policyPeriodEnd: policy.policyPeriodEnd || analysis.policyPeriodEnd,
      commercialPremium: analysis.commercialPremium,
      grossPremium: analysis.grossPremium,
      minPremium: analysis.minPremium,
      maxPremium: analysis.maxPremium,
      iof: analysis.iof,
      assistanceValue: analysis.assistanceValue,
      value: analysis.value,
      valueFirstInstallment: analysis.valueFirstInstallment,
      analysisInstallments: analysis.installments,
      validationStatus: analysis.validationStatus,
      validationMessage: analysis.validationMessage,
      coverages: analysis.coverages || []
    });
    setOpenAdminEditPolicyModal(true);
  };

  const handleCloseAdminEditPolicyModal = () => {
    setOpenAdminEditPolicyModal(false);
    setAdminEditPolicyData(undefined);
  };

  const handleCloseCancelModal = () => {
    setCurrentPolicyId('');
    setCurrentInsurerCode('');
    setOpenCancelModal(false);
  };

  const handleExportData = async () => {
    setIsLoadingData(true);

    try {
      if (!appliedFilters.start || !appliedFilters.end) {
        warningNotification(
          'Opss...',
          'Por favor, selecione o periodo que deseja gerar o relatório.'
        );

        return;
      }

      const filters = {
        page: currentPage,
        start: appliedFilters.start,
        end: appliedFilters.end,
        insurerId: appliedFilters.insurerId,
        estateId: appliedFilters.estateId,
        status: appliedFilters.status,
        accountManager: appliedFilters.accountManager,
        document: appliedFilters.document,
        name: appliedFilters.name,
        address: appliedFilters.address
      };

      const response = await getFireInsuranceReport(filters);

      if (!response) {
        dangerNotification(
          'Ops...',
          'Erro ao solicitar relatório de apólices de seguro incêndio.'
        );
      } else {
        setReportState(response);
        setReportModalIsOpen(true);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleSyntheticReport = async () => {
    setIsLoadingData(true);

    try {
      if (!appliedFilters.start || !appliedFilters.end) {
        warningNotification(
          'Opss...',
          'Por favor, selecione o periodo que deseja gerar o relatório.'
        );

        return;
      }

      const filters = {
        insurerId: appliedFilters.insurerId,
        estateId: appliedFilters.estateId,
        start: appliedFilters.start,
        end: appliedFilters.end
      };

      const response = await getFireInsuranceEstatesReport(filters);

      if (!response) {
        dangerNotification(
          'Ops...',
          'Erro ao solicitar relatório de apólices de seguro incêndio.'
        );
      } else {
        setReportState(response);
        setReportModalIsOpen(true);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleReportModalClose = () => {
    setReportModalIsOpen(false);
  };

  const { data: usersData, isLoading: isAccountManagersLoading } = useDropDownUsers(
    user?.broker?.id
  );

  const accountManagerOptions = useMemo(() => {
    if (!usersData) return [];

    const convertedValues = usersData.map((item: any) => ({
      id: item.id,
      name: item.name
    }));

    return UsersToCustomDropDownOptions(false, convertedValues).map((item: any) => ({
      value: String(item.value),
      text: String(item.text)
    }));
  }, [usersData]);

  const filterFields: FilterField[] = useMemo(() => {
    const fields: FilterField[] = [
      {
        kind: 'search',
        name: 'search',
        label: 'Pesquisar',
        placeholder: 'Digite...',
        defaultType: 'document',
        types: typeOptions.map(option => ({
          value: String(option.value),
          label: String(option.text)
        })),
        width: 1.6
      },
      {
        kind: 'multiSelect',
        name: 'insurerId',
        label: 'Seguradora',
        placeholder: 'Selecionar...',
        options: toSelectOptions(insurerOptions)
      }
    ];

    if (isBackOffice) {
      fields.push({
        kind: 'select',
        name: 'estateId',
        label: 'Imobiliária',
        placeholder: 'Selecionar...',
        options: toSelectOptions(estateOptions),
        getDisplayValue: values => {
          const option = estateOptions.find(
            item => String(item.value) === String(values.estateId)
          );
          return option ? option.text : undefined;
        }
      });
    }

    fields.push(
      {
        kind: 'multiSelect',
        name: 'status',
        label: 'Status',
        placeholder: 'Selecionar...',
        options: toSelectOptions(proposalStatusList)
      },
      {
        kind: 'dateRange',
        name: 'period',
        label: 'Período',
        startName: 'start',
        endName: 'end'
      },
      {
        kind: 'select',
        name: 'vigency',
        label: 'Expirar em',
        placeholder: 'Selecionar...',
        advanced: true,
        options: toSelectOptions(ExpireInList),
        getDisplayValue: values => {
          const option = ExpireInList.find(
            item => String(item.value) === String(values.vigency)
          );
          return option ? option.text : undefined;
        }
      },
      {
        kind: 'custom',
        name: 'estateUnitId',
        label: 'Unidade',
        advanced: true,
        getDisplayValue: values => {
          if (!values.estateUnitId) return undefined;
          if (values.estateUnitId === 'null') return 'Sem unidade';
          return String(values.estateUnitId);
        },
        render: ({ values, setValue }) => (
          <EstateUnitFilter
            estateId={String(values.estateId || user?.estate?.id || singleEstateId || '')}
            value={values.estateUnitId || ''}
            onChange={value => setValue('estateUnitId', value)}
            upward
          />
        )
      }
    );

    if (!user.role.includes('ESTATE')) {
      fields.push({
        kind: 'select',
        name: 'accountManager',
        label: 'Gestor de Contas',
        placeholder: 'Selecionar...',
        advanced: true,
        loading: isAccountManagersLoading,
        options: toSelectOptions(accountManagerOptions),
        getDisplayValue: values => {
          const option = accountManagerOptions.find(
            item => String(item.value) === String(values.accountManager)
          );
          return option ? option.text : undefined;
        }
      });
    }

    return fields;
  }, [
    isBackOffice,
    insurerOptions,
    estateOptions,
    accountManagerOptions,
    isAccountManagersLoading,
    user?.estate?.id,
    user.role,
    singleEstateId
  ]);

  const clearDeepLinkFromUrl = useCallback(() => {
    const currentPathname = history.location.pathname;
    const pathSegments = currentPathname.split('/');
    const lastSegment = pathSegments[pathSegments.length - 1];
    const nextPathname = isUuid(lastSegment)
      ? pathSegments.slice(0, -1).join('/') || '/admin/fire-insurance-policies'
      : currentPathname;

    const params = new URLSearchParams(history.location.search);
    params.delete('fireInsuranceId');
    const nextSearch = params.toString();
    const currentSearch = history.location.search.startsWith('?')
      ? history.location.search.slice(1)
      : history.location.search;

    if (nextPathname === currentPathname && nextSearch === currentSearch) {
      return;
    }

    history.replace({
      pathname: nextPathname,
      search: nextSearch ? `?${nextSearch}` : ''
    });
  }, [history]);

  useEffect(() => {
    if (!isBackOffice) {
      setIsEnabledToFetch(true);
    }
  }, [isBackOffice]);

  useEffect(() => {
    if (!isEnabledToFetch) return;
    getData();
  }, [currentPage, appliedFilters, isEnabledToFetch, getData]);

  useEffect(() => {
    if (appliedFilters.estateId) {
      getDataPolicyInfoAwaitingBilling(appliedFilters.estateId);
    } else {
      setInfoAwaitingBilling({
        qty: '-',
        amount: '-',
        batchClosingDay: []
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters.estateId]);

  const handleFilterSearch = (values: FilterValues) => {
    const next = normalizeToApiFilter(values);
    if (!next.estateId && singleEstateId) {
      next.estateId = singleEstateId;
    }
    if (fireInsuranceAnalysisIdRef.current || fireInsuranceIdRef.current) {
      clearDeepLinkFromUrl();
    }
    setFireInsuranceAnalysisId(null);
    setFireInsuranceId(null);
    setAppliedFilters(next);
    setCurrentPage(1);
    setIsEnabledToFetch(true);
  };

  const handleFilterClear = () => {
    setFireInsuranceAnalysisId(null);
    setFireInsuranceId(null);
    setCurrentPage(1);
    setIsEnabledToFetch(true);
    clearDeepLinkFromUrl();
  };

  async function handleAlfaModelExport() {
    setIsLoadingData(true);
    try {
      const selectedEstateRecord = appliedFilters.estateId
        ? estateRecordsById[String(appliedFilters.estateId)]
        : undefined;

      if (selectedEstateRecord?.code === 'ALFA') {
        warningNotification(
          'Opss...',
          'Não é possível exportar para o modelo Alfa com seguradora ALFA selecionada.'
        );
        return;
      }

      if (!selectedEstateRecord) {
        warningNotification(
          'Opss...',
          'Precisa selecionar uma seguradora para exportar com modelo Alfa.'
        );
        return;
      }

      const filters = {
        page: currentPage,
        start: appliedFilters.start,
        end: appliedFilters.end,
        insurerId: appliedFilters.insurerId,
        estateId: appliedFilters.estateId,
        status: appliedFilters.status,
        accountManager: appliedFilters.accountManager,
        document: appliedFilters.document,
        name: appliedFilters.name,
        address: appliedFilters.address
      };

      const response = await getFireInsuranceModelAlfaReport(filters);

      if (!response) {
        dangerNotification(
          'Ops...',
          'Erro ao solicitar relatório de apólices de seguro incêndio.'
        );
      }
      const link = document.createElement('a');
      link.href = response.url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoadingData(false);
    }
  }

  return (
    <>
      {reportModalIsOpen && reportState === 200 ? (
        <ReportModal
          isOpen={reportModalIsOpen}
          header="Relatório de Seguro Incêndio"
          message={`
              O relatório será gerado em background e será enviado o link para download via e-mail.
              Atenção, só será possível gerar um novo relatório após a finalização desse.
            `}
          onClose={handleReportModalClose}
        />
      ) : (
        <ReportModal
          isOpen={reportModalIsOpen}
          header="Relatório de Seguro Incêndio"
          message="Já existe um relatório semelhante na fila para ser gerado, aguarde a notificação via email."
          onClose={handleReportModalClose}
        />
      )}

      <Grid columns={'equal'}>
        <Grid.Row
          style={{ textAlign: 'right', justifyContent: 'right', paddingRight: 10 }}
        >
          <FireGeneralCondition
            openModal={openConditions}
            toggleOpen={toggleOpenConditions}
            toggleClose={toggleCloseConditions}
          />
        </Grid.Row>
      </Grid>

      <FilterBar
        fields={filterFields}
        initialValues={initialFilterValues}
        onSearch={handleFilterSearch}
        onClear={handleFilterClear}
        loading={isLoadingData}
        syncToUrl
        urlNamespace="fire-policies"
        hasExternalActiveFilters={Boolean(fireInsuranceAnalysisId || fireInsuranceId)}
        actions={
          <>
            {userRole === 'BROKER_ADMIN' &&
            Array.isArray(appliedFilters.insurerId) &&
            appliedFilters.insurerId.length === 1 ? (
              <Button
                light
                type="button"
                disabled={isLoadingData}
                loading={isLoadingData}
                onClick={handleSyntheticReport}
                content="Relatório Sintético"
                style={{ height: 38, minWidth: 130 }}
              />
            ) : null}
            {userRole === 'BROKER_ADMIN' &&
            Array.isArray(appliedFilters.insurerId) &&
            appliedFilters.insurerId.length === 1 ? (
              <Button
                light
                type="button"
                disabled={isLoadingData}
                loading={isLoadingData}
                onClick={handleAlfaModelExport}
                content="Exportar c/ Modelo Alfa"
                style={{ height: 38, minWidth: 160 }}
              />
            ) : null}
            <Button
              light
              type="button"
              disabled={isLoadingData}
              loading={isLoadingData}
              onClick={handleExportData}
              content="Exportar"
              style={{ height: 38, minWidth: 110 }}
            />
          </>
        }
      >
        {appliedFilters.estateId ? (
          <Grid>
            <Grid.Row verticalAlign="middle" style={{ padding: 0, margin: '15px 0' }}>
              <Grid columns={16} divided>
                <GridRow style={{ padding: 0, margin: 0 }}>
                  <GridColumn
                    width={4}
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      margin: '0 14px'
                    }}
                  >
                    <Icon name="pie graph" size="big" color="blue" />
                    <Item style={{ paddingLeft: '10px', margin: 0 }}>
                      <FieldLabel>Fechamento do lote</FieldLabel>
                      {infoAwaitingBilling.batchClosingDay.length ? (
                        infoAwaitingBilling.batchClosingDay.map((closingDay: any) => {
                          return (
                            <>
                              <FieldLabel
                                key={closingDay.name}
                                style={{ color: '#1678c2' }}
                              >
                                {closingDay.name}: Dia {closingDay.closingDay}
                              </FieldLabel>
                            </>
                          );
                        })
                      ) : (
                        <FieldLabel style={{ color: '#1678c2' }}>-</FieldLabel>
                      )}
                    </Item>
                  </GridColumn>

                  <GridColumn width={4}>
                    <FieldLabel>Total de itens a faturar</FieldLabel>
                    <FieldLabel style={{ color: '#1678c2' }}>
                      {infoAwaitingBilling.qty}
                    </FieldLabel>
                  </GridColumn>

                  <GridColumn width={4}>
                    <FieldLabel>Valor total a faturar</FieldLabel>
                    <FieldLabel style={{ color: '#1678c2' }}>
                      {infoAwaitingBilling.amount}
                    </FieldLabel>
                  </GridColumn>
                </GridRow>
              </Grid>
            </Grid.Row>
          </Grid>
        ) : null}
      </FilterBar>

      <Table celled>
        <Table.Header fullWidth>
          <Table.HeaderCell textAlign={'center'}>Criado em</Table.HeaderCell>
          <Table.HeaderCell textAlign={'center'}>Seguradora</Table.HeaderCell>
          <Table.HeaderCell textAlign={'center'}>Imobiliária</Table.HeaderCell>
          <Table.HeaderCell textAlign={'center'}>Inquilino</Table.HeaderCell>
          <Table.HeaderCell textAlign={'center'}>Endereço</Table.HeaderCell>
          <Table.HeaderCell textAlign={'center'}>Vigência</Table.HeaderCell>
          <Table.HeaderCell textAlign={'center'}>Status</Table.HeaderCell>
          <Table.HeaderCell textAlign={'center'}>Prêmio</Table.HeaderCell>
          <Table.HeaderCell textAlign={'center'}>Ações</Table.HeaderCell>
        </Table.Header>
        <Table.Body>
          {isLoadingData ? (
            <Table.Row>
              <Table.Cell textAlign={'center'} colSpan={9}>
                <Loader active inline />
              </Table.Cell>
            </Table.Row>
          ) : (
            <>
              {data.length ? (
                data.map((item: any) => {
                  const { fireInsuranceAnalysis } = item;
                  const { fireInsurance } = fireInsuranceAnalysis;
                  const address = fireInsurance.propertyAddresses;
                  const [date, hour] =
                    item.createdAt &&
                    moment(item.createdAt).format('DD/MM/YYYY HH:mm').split(' ');

                  return (
                    <ClickableTableRow key={item.id}>
                      <ClickableTableCell
                        textAlign={'center'}
                        onClick={() => renderDetailModal(item)}
                      >
                        {date}
                        <br />
                        {hour}
                      </ClickableTableCell>

                      <ClickableTableCell
                        textAlign={'center'}
                        onClick={() => renderDetailModal(item)}
                      >
                        {fireInsuranceAnalysis.insurer.name}
                      </ClickableTableCell>

                      <ClickableTableCell
                        textAlign={'center'}
                        onClick={() => renderDetailModal(item)}
                      >
                        {fireInsurance.estate.name}
                      </ClickableTableCell>

                      <ClickableTableCell
                        textAlign={'center'}
                        onClick={() => renderDetailModal(item)}
                      >
                        {fireInsurance.tenantName}
                        <br />
                        <span style={{ color: '#808080' }}>
                          ({maskCpfOrCnpj(fireInsurance.tenantDocument)})
                        </span>
                      </ClickableTableCell>

                      <ClickableTableCell
                        textAlign={'center'}
                        onClick={() => renderDetailModal(item)}
                      >
                        {address.street}, {address.number}
                        <br />
                        {address.district} - {address.city}, {address.state}
                      </ClickableTableCell>

                      <ClickableTableCell
                        width={1}
                        textAlign={'center'}
                        onClick={() => renderDetailModal(item)}
                      >
                        <p style={{ color: 'gray', padding: 0, margin: 0 }}>
                          {item.policyPeriodStart
                            ? moment(
                              adjustDateForTimezone(item.policyPeriodStart)
                            ).format('DD/MM/YYYY')
                            : null}
                        </p>
                        <p style={{ color: '#964800', padding: 0, margin: 0 }}>
                          {item.policyPeriodEnd
                            ? moment(adjustDateForTimezone(item.policyPeriodEnd)).format(
                              'DD/MM/YYYY'
                            )
                            : null}
                        </p>
                      </ClickableTableCell>

                      <ClickableTableCell
                        textAlign={'center'}
                        onClick={() => renderDetailModal(item)}
                      >
                        {item.status && translateProposalStatus(item.status)}
                        {(fireInsuranceAnalysis as any)?.isConsultative && (
                          <div style={{ marginTop: 4 }}>
                            <Label size="mini" color="purple" basic>
                              Consultivo
                            </Label>
                          </div>
                        )}
                      </ClickableTableCell>

                      <ClickableTableCell
                        width={1}
                        textAlign={'center'}
                        onClick={() => renderDetailModal(item)}
                      >
                        <strong>
                          R$: {maskMoney(fireInsuranceAnalysis.grossPremium, true)}
                        </strong>
                        <br />
                        <span style={{ color: '#808080', fontSize: '11px' }}>
                          ({fireInsuranceAnalysis.installments}x) R$:{' '}
                          {fireInsuranceAnalysis.value}
                        </span>
                      </ClickableTableCell>

                      <Table.Cell textAlign={'center'}>
                        <Menu compact>
                          <Menu.Item>
                            <AuthorizationContainer
                              action={ActionEnum.download}
                              feature={FeatureEnum.files}
                            >
                              <Popup
                                content={'Gerenciar Anexos'}
                                open={popupString === item.id}
                                trigger={
                                  <ModalAttachFiles
                                    bondInsuranceId={
                                      item.fireInsuranceAnalysis.fireInsuranceId
                                    }
                                    tableName={'fire_insurance'}
                                    fileIdentifiers={fileIdentifiers}
                                    brokerOnly={true}
                                    message={''}
                                    trigger={
                                      <Button
                                        icon
                                        color="teal"
                                        onMouseEnter={() => setPopupString(item.id)}
                                        onMouseLeave={() => setPopupString('')}
                                      >
                                        <Icon name={'attach'} />
                                      </Button>
                                    }
                                  />
                                }
                              />
                            </AuthorizationContainer>
                          </Menu.Item>

                          <Menu.Item>
                            <Dropdown
                              trigger={<IconButton name="ellipsis vertical" />}
                              icon={null}
                              direction="left"
                            >
                              <Dropdown.Menu style={{ zIndex: 1000 }}>
                                {canEditPolicy(item) && (
                                  <AuthorizationContainer
                                    action={ActionEnum.update}
                                    feature={FeatureEnum.fireinsurances}
                                  >
                                    <Dropdown.Item
                                      icon="pencil"
                                      text={'Editar Apólice'}
                                      onClick={() => handleOpenEditPolicyModal(item)}
                                    />
                                  </AuthorizationContainer>
                                )}

                                {userRole === UserRolesEnum.brokerAdmin && (
                                  <AuthorizationContainer
                                    action={ActionEnum.updatePolicy}
                                    feature={FeatureEnum.fireinsurances}
                                  >
                                    <Dropdown.Item
                                      icon="settings"
                                      text={'Editar Apólice'}
                                      onClick={() => handleOpenAdminEditPolicyModal(item)}
                                    />
                                  </AuthorizationContainer>
                                )}

                                {userRole === UserRolesEnum.brokerAdmin &&
                                  (fireInsuranceAnalysis as any)?.isConsultative &&
                                  item.status === 'POLICY_CREATING' && (
                                    <AuthorizationContainer
                                      action={ActionEnum.updatePolicy}
                                      feature={FeatureEnum.fireinsurances}
                                    >
                                      <Dropdown.Item
                                        icon="check circle"
                                        text={'Emitir apólice consultiva'}
                                        onClick={() => setConsultativeIssueItem(item)}
                                      />
                                    </AuthorizationContainer>
                                  )}

                                {canRetryHire(item) && (
                                  <AuthorizationContainer
                                    action={ActionEnum.updatePolicy}
                                    feature={FeatureEnum.fireinsurances}
                                  >
                                    <Dropdown.Item
                                      icon="redo"
                                      text={'Tentar Contratar Novamente'}
                                      onClick={() => handleOpenRetryHireConfirm(item)}
                                    />
                                  </AuthorizationContainer>
                                )}

                                <Dropdown.Item
                                  icon="refresh"
                                  text={'Solicitar Renovação'}
                                  onClick={() => {
                                    updateCurrentPage({
                                      id: 4,
                                      clickHandler: (e: any, data: any) => { },
                                      name: 'fire-insurance-budget',
                                      route: 'fire-insurance-budget',
                                      text: 'Cotação',
                                      title: 'Novo orçamento de seguro incêndio'
                                    });
                                    history.push(
                                      `/admin/fire-insurance-budget/${item.fireInsuranceAnalysis?.id}?context=${BudgetContext.renewal}&policyOrigin=${fireInsuranceAnalysis.insurer.code}&estateId=${fireInsurance.estateId}`
                                    );
                                  }}
                                />

                                <Dropdown.Item
                                  icon="trash"
                                  text={'Solicitar Cancelamento'}
                                  disabled={
                                    // item.updatedAt === null ||
                                    item.status === 'CANCELED' ||
                                    item.status === 'CANCELATION_REQUEST'
                                    // || differenceInHours(today, lastUpdate) < -1
                                  }
                                  onClick={() => handleOpenCancelModal(item)}
                                />

                                {(item.status === 'CANCELED' ||
                                  item.status === 'CANCELATION_REQUEST') && (
                                    <AuthorizationContainer
                                      action={ActionEnum.update}
                                      feature={FeatureEnum.fireinsurances}
                                    >
                                      <Dropdown.Item
                                        icon="edit"
                                        text={'Editar Cancelamento'}
                                        disabled={
                                          item.status !== 'CANCELED' &&
                                          item.status !== 'CANCELATION_REQUEST'
                                        }
                                        onClick={() => handleOpenEditCancelModal(item)}
                                      />
                                    </AuthorizationContainer>
                                  )}

                                <Dropdown.Item
                                  icon="shield"
                                  text={'Cotar Seguro Fiança'}
                                  onClick={() => {
                                    PushRoute(history, props, `bond-insurance`, {
                                      fireInsuranceId:
                                        item.fireInsuranceAnalysis.fireInsuranceId
                                    });
                                  }}
                                />
                                {lenderInsuranceEnabled && (() => {
                                  const tenantDocument = (fireInsurance.tenantDocument || '').replace(/\D/g, '');
                                  const isTenantPf = tenantDocument.length === 11;
                                  if (!isTenantPf) return null;

                                  const tenantBirthDate = fireInsurance.tenantBirthdate || '';
                                  const dateStr = tenantBirthDate instanceof Date
                                    ? tenantBirthDate.toISOString().split('T')[0]
                                    : String(tenantBirthDate);
                                  const tenantAge = dateStr ? calculateAge(dateStr) : 0;
                                  if (tenantAge >= 65) return null;

                                  return (
                                    <Dropdown.Item
                                      icon="handshake"
                                      text={fireInsurance.lenderInsuranceId ? "Gerenciar Seguro Prestamista" : "Adicionar Seguro Prestamista"}
                                      onClick={() => handleOpenLenderInsuranceModal(item.fireInsuranceAnalysis)}
                                    />
                                  );
                                })()}
                              </Dropdown.Menu>
                            </Dropdown>
                            <Label floating style={{ background: 'none' }}>
                              {item.fireInsuranceAnalysis.validationStatus ===
                                'warning' ||
                                item.fireInsuranceAnalysis.validationStatus === 'error' ? (
                                <Popup
                                  content={
                                    item.fireInsuranceAnalysis.validationMessage ??
                                    'Possui inconsistência'
                                  }
                                  trigger={
                                    <IconButton color="yellow" name="warning sign" />
                                  }
                                />
                              ) : (
                                <></>
                              )}
                            </Label>
                          </Menu.Item>
                        </Menu>
                      </Table.Cell>
                    </ClickableTableRow>
                  );
                })
              ) : (
                <Table.Row>
                  <Table.Cell colSpan={9} textAlign={'center'}>
                    {getListEmptyMessage(props.user?.role, props.user?.estateUnitId)}
                  </Table.Cell>
                </Table.Row>
              )}
            </>
          )}
        </Table.Body>

        <Table.Footer fullWidth>
          <Table.Row>
            <Table.HeaderCell colSpan={9} textAlign={'right'}>
              {!isLoadingData && (
                <TablePaginationMenu
                  currentPage={currentPage}
                  dataCount={count}
                  setCurrentPage={setCurrentPage}
                />
              )}
            </Table.HeaderCell>
          </Table.Row>
        </Table.Footer>
      </Table>

      <DetailModal
        fireInsurancePolicy={{ ...currentDetailPolicy, policyCancelation }}
        open={openModal}
        onClose={handleCloseModal}
        key={'detailPolicy'}
      />

      <CancelPolicyModal
        open={openCancelModal}
        close={handleCloseCancelModal}
        policyId={currentPolicyId}
        insurerCode={currentInsurerCode}
        policyData={currentPolicyData}
      />

      {openEditCancelModal && (
        <EditCancelPolicyModal
          open={openEditCancelModal}
          close={handleCloseEditCancelModal}
          data={editCancelData}
        />
      )}

      {openEditPolicyModal && editPolicyData && (
        <EditPolicyModal
          open={openEditPolicyModal}
          close={handleCloseEditPolicyModal}
          data={editPolicyData}
          onSuccess={getData}
        />
      )}

      {openAdminEditPolicyModal && adminEditPolicyData && (
        <AdminEditPolicyModal
          open={openAdminEditPolicyModal}
          close={handleCloseAdminEditPolicyModal}
          data={adminEditPolicyData}
          onSuccess={getData}
        />
      )}

      {consultativeIssueItem && (
        <ConsultativeIssueModal
          open={Boolean(consultativeIssueItem)}
          item={consultativeIssueItem}
          onClose={() => setConsultativeIssueItem(null)}
          onSaved={getData}
        />
      )}

      {openLenderInsuranceModal && selectedAnalysis && (
        <LenderInsuranceUpdateModal
          open={openLenderInsuranceModal}
          onClose={handleCloseLenderInsuranceModal}
          onSuccess={onSuccessLenderInsurance}
          data={selectedAnalysis}
          rate={lenderInsuranceRate}
          isTenantPF={(selectedAnalysis?.fireInsurance?.tenantDocument || '').replace(/\D/g, '').length === 11}
        />
      )}

      <Confirm
        open={retryHireConfirmOpen}
        header="Tentar Contratar Novamente"
        content={
          policyToRetryHire && canRetryYelumHire(policyToRetryHire)
            ? 'Vamos conferir se a contratação já existe na Yelum e, se não existir, tentar contratar novamente.'
            : 'Deseja tentar contratar esta apólice novamente?'
        }
        cancelButton="Cancelar"
        confirmButton={isRetryingHire ? 'Aguarde...' : 'Tentar novamente'}
        onCancel={handleCloseRetryHireConfirm}
        onConfirm={handleConfirmRetryHire}
      />

      <PopupModal
        locationActivation={BannerLocationActivationEnum.FIRE_INSURANCE_POLICY_LIST}
      />
    </>
  );
};

const mapState = (state: iRootState) => ({
  availableBrokersAndEstates: state.availableBrokersAndEstates,
  user: state.user,
  currentPage: state.currentPage
});

const mapDispatch = (dispatch: iRootDispatch) => ({
  updateAvailableBrokersAndEstates: (availableBrokers: AvailableBrokersAndEstatesState) =>
    dispatch.availableBrokersAndEstates.updateAvailableBrokersAndEstates(
      availableBrokers
    ),
  updateCurrentPage: (state: CurrentPageState) =>
    dispatch.currentPage.updateCurrentPage(state)
});

export default connect(mapState, mapDispatch)(FireInsurancePolicy);
